<script setup>
import { getUserInfoAPI, updateAdditionalInfoAPI, getAdditionalInfoTypesAPI } from '@/api/myPage';
import { fastSearch, fastSearchByMySaju, logGuestUserHistory } from '@/api/Eoway';
import { addContentLogAPI } from '@/api/logServiceApi';
import { userGroupSave2 } from '@/api/Eoway';
import {
  getManualCampaignsAPI,
  getCampaignEligibilityAPI,
} from '@/api/CampainApi';
import { usePaymentsStore } from '@/store/modules/payments';
import { usePrimarySajuStore } from '@/store/modules/primarySaju';
import fiveMixin from '@/composables/fiveMixin';
import useFunctions from '@/composables/useFunctions';
import MoreInfoProgress from '@/components/mypage/moreinfo/MoreInfoProgress.vue';
import MoreInfoItemCard from '@/components/mypage/moreinfo/MoreInfoItemCard.vue';
import MoreInfoConfirmModal from '@/components/mypage/moreinfo/MoreInfoConfirmModal.vue';

import MoreInfoPointGuide from '@/components/mypage/moreinfo/MoreInfoPointGuide.vue';
import SajuForm from '@/components/saju/SajuForm.vue';
import PopArea from '@/components/saju/PopArea.vue';
import PopGanji from '@/components/saju/PopGanji.vue';
import MobileHeader from '@/components/common/MobileHeader.vue';
import Snackbar from '@/components/common/Snackbar.vue';
import AlertComponent from '@/components/common/Alert.vue';
import { CardHeader } from '@/components/mypage';
import { useSnackbar } from '@/composables/useSnackbar';
import { useDialogManager } from '@/composables/useDialogManager';
import { useBackLayer, useBackReducer } from '@/composables/useAppBack';
import { DEV_MODE } from '@/config/dev-mode';

const { ...five } = fiveMixin();
const { ...useFun } = useFunctions();
const paymentsStore = usePaymentsStore();
const primarySajuStore = usePrimarySajuStore();

definePage({ meta: { layout: 'mypage' } });

const router = useRouter();
const { snackbarRef, triggerSnackbar } = useSnackbar();
const { alertRef, dialogTitle, dialogText, showAlert } = useDialogManager();

// ─── 항목 데이터 정의 ───
const ITEMS = [
  {
    id: 'saju',
    label: '내 사주',
    points: 250,
    icon: '🔮',
    desc: '내 사주를 등록하면 나만의 낭만브리핑이 시작돼요',
    bonusTag: '🎂 매년 생일에 1,000P 선물!',
    type: 'saju',
  },
  {
    id: 'mbti',
    label: 'MBTI',
    points: 50,
    icon: '🧠',
    desc: '내가 생각하는 나와 사주가 보는 나는 얼마나 다를까요?',
    type: 'mbti',
  },
  {
    id: 'blood',
    label: '혈액형',
    points: 30,
    icon: '💧',
    desc: '사주와 함께 보면 나를 더 깊이 이해할 수 있어요',
    type: 'blood',
  },
  {
    id: 'channel',
    label: '가입경로',
    points: 10,
    icon: '📍',
    desc: '낭만만세력을 어떻게 알게 되셨나요?',
    type: 'channel',
  },
  {
    id: 'interest',
    label: '사주 관심도',
    points: 30,
    icon: '⭐',
    desc: '평소 사주를 어떻게 접하고 계세요?',
    type: 'interest',
  },
  {
    id: 'understanding',
    label: '사주 이해도',
    points: 30,
    icon: '📖',
    desc: '사주가 얼마나 익숙하세요? 더 나은 서비스를 위한 참고가 돼요',
    type: 'understanding',
  },
];

const TOTAL_POINTS = 400;

// 부가정보 항목 id → 캠페인 name 매핑
const ITEM_CAMPAIGN_NAME_MAP = {
  saju: 'my_saju',
  mbti: 'mbti',
  blood: 'blood_type',
  channel: 'join_path',
  interest: 'interest',
  understanding: 'understanding',
};
const ALL_COMPLETE_CAMPAIGN_NAME = '부가정보';

// 캠페인 name → campaignId 매핑 (API 조회 후 채움)
const campaignIdMap = ref({});

// 항목별 포인트 수령 완료 여부 (eligibility.canParticipate === false)
const pointsClaimed = ref({});

/** 캠페인 목록 조회 후 name → campaignId 매핑 저장 */
const loadCampaigns = async () => {
  try {
    const result = await getManualCampaignsAPI();
    if (result?.content) {
      result.content.forEach((c) => {
        campaignIdMap.value[c.name] = c.campaignId;
      });
    }
  } catch (e) {
    DEV_MODE && console.error('캠페인 목록 조회 실패:', e);
  }
};

/** 크레딧바 갱신 (포인트 + 분석권) */
const refreshCreditBar = async () => {
  await Promise.allSettled([paymentsStore.getOwnPoint(), paymentsStore.fetchTicketCount()]);
};

/** 모든 항목의 캠페인 eligibility 조회 → pointsClaimed 갱신
 *  - reasonCode가 ALREADY_PARTICIPATED인 경우에만 수령 완료로 간주.
 *  - CAMPAIGN_NOT_AVAILABLE 등 그 외 사유는 미수령 상태 유지 (정보는 저장됐지만 이벤트 참여 실패)
 *  - 데이터 삭제 후 재진입 시에도 서버 이력 기준으로 진행바가 유지되도록 모든 항목을 조회한다.
 *  - 항목 id → { claimed, reasonCode } 맵 반환
 */
const refreshCampaignEligibility = async () => {
  const targetIds = ITEMS.map((item) => item.id);
  const eligibilityMap = {};
  const results = await Promise.allSettled(
    targetIds.map(async (id) => {
      const campaignName = ITEM_CAMPAIGN_NAME_MAP[id];
      const campaignId = campaignIdMap.value[campaignName];
      if (!campaignId) return { id, claimed: false, reasonCode: null };
      try {
        const res = await getCampaignEligibilityAPI(campaignId);
        const claimed = res?.reasonCode === 'ALREADY_PARTICIPATED';
        return { id, claimed, reasonCode: res?.reasonCode || null };
      } catch (e) {
        DEV_MODE && console.error(`eligibility 조회 실패 (${campaignName}):`, e);
        return { id, claimed: false, reasonCode: null };
      }
    }),
  );
  results.forEach((r) => {
    if (r.status === 'fulfilled' && r.value) {
      pointsClaimed.value[r.value.id] = r.value.claimed;
      eligibilityMap[r.value.id] = r.value;
    }
  });
  return eligibilityMap;
};

const MBTI_MAP = {
  에너지방향: {
    label: '에너지 방향',
    opts: [
      ['E', '외향'],
      ['I', '내향'],
    ],
  },
  인식방식: {
    label: '인식 방식',
    opts: [
      ['S', '감각'],
      ['N', '직관'],
    ],
  },
  판단: {
    label: '판단',
    opts: [
      ['T', '사고'],
      ['F', '감정'],
    ],
  },
  생활양식: {
    label: '생활 양식',
    opts: [
      ['J', '판단'],
      ['P', '인식'],
    ],
  },
};
const BLOOD_TYPES = ['A', 'B', 'AB', 'O'];
const RH_OPTIONS = ['Rh+', 'Rh-'];
// API에서 받아오는 선택지 (sortOrder 오름차순 정렬, { value, label })
const channelOptions = ref([]);
const interestOptions = ref([]);
const understandingOptions = ref([]);

// ─── 상태 ───
const completed = ref({});
const savedData = ref({});
const expandedId = ref(null);

const isWebView = ref(false);
const isUserInfoLoaded = ref(false);
const isEligibilityLoaded = ref(false);
const insetBottom = ref(20);
const celebrationDismissed = ref(false);

// 패널 입력 상태
const mbtiInput = ref({});
const bloodInput = ref('');
const rhInput = ref('');
const channelInput = ref('');
const interestInput = ref('');
const understandingInput = ref('');

// ─── 사주 입력 상태 ───
const sajuStep = ref('input'); // 'input' | 'result' | 'view'
const sajuViewMode = ref(false); // 등록된 사주 조회 모드
const sajuLoading = ref(false);
const sajuResult = ref(null); // fastSearch 결과
const sajuSaving = ref(false);
const sajuAreaTxt = ref('');
const sajuIsOptionInput = ref(true);
const isPopAreaBox = ref(false);
const isPopGanjiBox = ref(false);

const handleOpenAreaPopup = () => {
  isPopAreaBox.value = true;
};

const handleOpenGanjiPopup = () => {
  isPopGanjiBox.value = true;
};

const handleGanjiSearchInput = (item) => {
  Object.assign(sajuFormData.value, item);
  const timeParts = item.time.split(':');
  sajuFormData.value.hour = timeParts[0];
  sajuFormData.value.min = timeParts[1];
};

const selectAreaFinish = (selectAreaItem) => {
  const item = selectAreaItem.value;
  const txt = `${item.korCountryName}, ${item.korCityName} (${item.longitudeType} ${item.degrees}도 ${item.minutes}분)`;
  sajuAreaTxt.value = txt;
  sajuFormData.value = {
    ...sajuFormData.value,
    bornAreaId: `${item.id}`,
    addressTxt: txt,
    option4: true,
  };
  isPopAreaBox.value = false;
};

const createSajuFormData = () => ({
  year: '',
  month: '',
  day: '',
  hour: '',
  min: '',
  gender: null,
  userName: '',
  isLunar: 'false',
  isLeapYear: null,
  birth_time: null,
  birth: null,
  birthTime: null,
  unknownTime: null,
  bornAreaId: null,
  addressTxt: null,
  option1: true,
  option2: false,
  option3: false,
  option4: true,
  lang: 'KOR',
  langYN: 'N',
  userGroupId: null,
  shortly: false,
  blCheck: 'N',
});

const sajuFormData = ref(createSajuFormData());

const isCheckboxChecked = computed(() => !!sajuFormData.value.birth_time);

const canSajuLookup = computed(() => {
  const f = sajuFormData.value;
  return f.gender !== null && f.year && f.month && f.day && (f.birth_time || (f.hour && f.min));
});

const resetSajuInputs = () => {
  sajuStep.value = 'input';
  sajuFormData.value = createSajuFormData();
  sajuAreaTxt.value = '';
  sajuIsOptionInput.value = true;
  sajuResult.value = null;
};

/** 사주 조회 */
const handleSajuLookup = async () => {
  if (!canSajuLookup.value || sajuLoading.value) return;

  const f = sajuFormData.value;
  const year = f.year;
  const month = f.month.padStart(2, '0');
  const day = f.day.padStart(2, '0');
  const isBirthTimeUnknown = !!f.birth_time;
  const hour = isBirthTimeUnknown ? '25' : (f.hour || '').padStart(2, '0');
  const min = isBirthTimeUnknown ? '00' : (f.min || '').padStart(2, '0');

  if (parseInt(year) < 1900 || parseInt(year) > 2100) {
    alert('연도는 1900~2100 사이로 입력해주세요.');
    return;
  }
  if (!useFun.checkDateTimeValid({ checkType: 'date', year, month, day })) {
    alert('올바른 생년월일을 입력해주세요.');
    return;
  }
  if (!isBirthTimeUnknown && !useFun.checkDateTimeValid({ checkType: 'time', hour, min })) {
    alert('올바른 시간을 입력해주세요.');
    return;
  }

  sajuLoading.value = true;
  try {
    const params = {
      year,
      month,
      day,
      hour,
      min,
      birth: `${year}-${month}-${day}`,
      birthTime: isBirthTimeUnknown ? '25:00' : `${hour}:${min}`,
      gender: f.gender === '1' ? 1 : 0,
      userName: f.userName || '',
      isLunar: f.isLunar === 'true' ? true : f.isLeapYear ? null : false,
      isLeapYear: f.isLeapYear || null,
      unknownTime: isBirthTimeUnknown ? 'Y' : 'N',
      option1: f.option1,
      option2: f.option2,
      option4: f.option4,
      bornAreaId: f.bornAreaId,
      addressTxt: f.addressTxt,
    };

    const data = await fastSearch(params);
    if (data?.errmsg?.code === 1) {
      alert(data.errmsg.msg || '조회에 실패했습니다.');
      return;
    }
    logGuestUserHistory({ ...params, option5: '부가정보' });
    sajuResult.value = data;
    addContentLogAPI({ contentType: 'M_saju_check', option3: '부가정보' });
    sajuStep.value = 'result';
  } catch (e) {
    DEV_MODE && console.error('사주 조회 오류:', e);
    alert('조회 중 오류가 발생했습니다.');
  } finally {
    sajuLoading.value = false;
  }
};

/** 간지 객체에서 value 추출 (문자열이면 그대로, 객체면 .value) */
const getGanjiValue = (item) => {
  if (!item) return '-';
  if (typeof item === 'string') return item;
  return item.value || '-';
};

/** 사주 간지 컬럼 (PopSajuInfo 참고) */
const sajuGanjiColumns = computed(() => {
  if (!sajuResult.value?.birthganji) return [];
  const bg = sajuResult.value.birthganji;
  const toHangul = (char, pos) => five.getHanguelTxt(char, pos) || char;

  const columns = [];

  // 대운 (있으면)
  if (sajuResult.value.daeuns) {
    const curlucknum = sajuResult.value.transdata?.curlucknum || 0;
    const daeunNum =
      curlucknum > 100 ? Math.max(...sajuResult.value.daeuns.sky.map((da) => da.num)) : curlucknum;
    const skyItem = sajuResult.value.daeuns.sky.find((da) => da.num === daeunNum);
    const earthItem = sajuResult.value.daeuns.earth.find((da) => da.num === daeunNum);
    if (skyItem && earthItem) {
      columns.push({
        label: `${daeunNum}대운`,
        sky: toHangul(getGanjiValue(skyItem), 'sky'),
        earth: toHangul(getGanjiValue(earthItem), 'earth'),
      });
    }
  }

  // 시일월년 — birthganji.sky.hour 등은 { value: '...' } 객체
  columns.push(
    {
      label: '시',
      sky: toHangul(getGanjiValue(bg.sky.hour), 'sky'),
      earth: toHangul(getGanjiValue(bg.earth.hour), 'earth'),
    },
    {
      label: '일',
      sky: toHangul(getGanjiValue(bg.sky.day), 'sky'),
      earth: toHangul(getGanjiValue(bg.earth.day), 'earth'),
    },
    {
      label: '월',
      sky: toHangul(getGanjiValue(bg.sky.month), 'sky'),
      earth: toHangul(getGanjiValue(bg.earth.month), 'earth'),
    },
    {
      label: '년',
      sky: toHangul(getGanjiValue(bg.sky.year), 'sky'),
      earth: toHangul(getGanjiValue(bg.earth.year), 'earth'),
    },
  );

  return columns;
});

/** 새 사주등록 버튼 클릭: 뷰 모드 → 입력 모드 전환 */
const startNewSajuInput = () => {
  sajuViewMode.value = false;
  editingId.value = 'saju';
  resetSajuInputs();
};

/** 대표사주만 있고 내 사주는 없는 상태 (대표사주를 내 사주로 바로 등록 가능) */
const hasPrimarySajuOnly = computed(
  () => !savedData.value.saju?.mySaju && !!primarySajuStore.primarySaju,
);

/** 저장된 사주로 fastSearch 재조회 (뷰 모드 진입용). source: 'my' | 'primary' */
const loadSavedSajuResult = async (source = 'my') => {
  const mySaju =
    source === 'primary' ? primarySajuStore.primarySaju : savedData.value.saju?.mySaju;
  if (!mySaju) return;

  // birthday가 없으면 solar 또는 birth에서 파싱
  const birthday = mySaju.birthday || mySaju.solar || mySaju.birth || '';
  const [year, month, day] = birthday.split('-');
  const isBirthTimeUnknown = mySaju.birthTime === '25:00';
  const [bHour, bMin] = isBirthTimeUnknown
    ? ['25', '00']
    : (mySaju.birthTime || '00:00').split(':');

  // sajuFormData 복원 (결과 요약 텍스트에서 사용)
  sajuFormData.value = {
    ...createSajuFormData(),
    year: year || '',
    month: (month || '').replace(/^0/, ''),
    day: (day || '').replace(/^0/, ''),
    hour: isBirthTimeUnknown ? '' : bHour || '',
    min: isBirthTimeUnknown ? '' : bMin || '',
    gender: mySaju.gender === 'MEN' ? '1' : '0',
    userName: mySaju.guestUserName || '',
    isLunar: mySaju.isLuna === true ? 'true' : 'false',
    isLeapYear: mySaju.isLeapYear || null,
    birth_time: isBirthTimeUnknown ? 'unknown' : null,
    option1: mySaju.option1 ?? true,
    option2: mySaju.option2 ?? false,
    option4: mySaju.option4 ?? true,
    bornAreaId: mySaju.bornAreaId || null,
  };

  // handleEdit에서 이미 설정했을 수 있으므로 보장
  sajuResult.value = null;
  sajuStep.value = 'result';
  sajuLoading.value = true;

  try {
    const isMine =
      source === 'my' ||
      mySaju.isMySaju === true ||
      primarySajuStore.isMySaju(Number(mySaju.guestUserId));
    const data = isMine
      ? await fastSearchByMySaju(Number(mySaju.guestUserId), mySaju.guestUserName || '')
      : await fastSearch({
          year,
          month: (month || '').padStart(2, '0'),
          day: (day || '').padStart(2, '0'),
          hour: bHour,
          min: bMin,
          birth: birthday,
          birthTime: isBirthTimeUnknown ? '25:00' : `${bHour}:${bMin}`,
          gender: mySaju.gender === 'MEN' ? 1 : 0,
          userName: mySaju.guestUserName || '',
          isLunar: mySaju.isLuna === true ? true : mySaju.isLeapYear ? null : false,
          isLeapYear: mySaju.isLeapYear || null,
          unknownTime: isBirthTimeUnknown ? 'Y' : 'N',
          option1: mySaju.option1 ?? true,
          option2: mySaju.option2 ?? false,
          option4: mySaju.option4 ?? true,
          bornAreaId: mySaju.bornAreaId,
          addressTxt: null,
        });
    if (data?.errmsg?.code === 1) {
      sajuStep.value = 'input';
      return;
    }
    sajuResult.value = data;
    sajuStep.value = 'result';
  } catch (e) {
    DEV_MODE && console.error('저장된 사주 재조회 오류:', e);
    sajuStep.value = 'input';
  } finally {
    sajuLoading.value = false;
  }
};

/** 사주 결과 요약 텍스트 */
const sajuResultSummary = computed(() => {
  if (!sajuResult.value) return '';
  const f = sajuFormData.value;
  const gender = f.gender === '0' ? '여' : '남';
  const td = sajuResult.value.transdata;
  const manAge = td?.man_age ?? '';
  const korAge = td?.kor_age ?? '';
  const solar = sajuResult.value.solar || '';
  const isBirthTimeUnknown = !!f.birth_time;
  const time = isBirthTimeUnknown
    ? '시간미상'
    : `${(f.hour || '').padStart(2, '0')}:${(f.min || '').padStart(2, '0')}`;
  return `${f.userName ? f.userName + ' · ' : ''}${gender} ${korAge !== '' ? korAge + '세' : ''}${manAge !== '' ? '(만' + manAge + '세)' : ''} · ${solar} ${time}`;
});

/** 사주 등록 (저장) */
const handleSajuRegister = async () => {
  if (sajuSaving.value || !sajuResult.value) return;
  const wasSajuCompleted = !!completed.value.saju;
  const wasSajuClaimed = !!pointsClaimed.value.saju;
  sajuSaving.value = true;
  try {
    const f = sajuFormData.value;
    const isBirthTimeUnknown = !!f.birth_time;
    const isLunar = f.isLunar === 'true' ? true : f.isLeapYear ? null : false;
    const hour = isBirthTimeUnknown ? '25' : (f.hour || '').padStart(2, '0');
    const min = isBirthTimeUnknown ? '00' : (f.min || '').padStart(2, '0');

    // birth: 양/음력에 따라 solar 또는 lunar 날짜
    const rd = sajuResult.value;
    const birth = isLunar ? rd.lunar : rd.solar;

    // sajugangi: 천간4자 + 지지4자
    const bg = rd.birthganji;
    const gv = getGanjiValue;
    const sajugangi = `${gv(bg.sky.hour)}${gv(bg.sky.day)}${gv(bg.sky.month)}${gv(bg.sky.year)}${gv(bg.earth.hour)}${gv(bg.earth.day)}${gv(bg.earth.month)}${gv(bg.earth.year)}`;
    const luck = `${gv(bg.sky.luck)}${gv(bg.earth.luck)}`;

    // 기존 사주와 동일하면 API 호출 없이 닫기
    if (wasSajuCompleted && savedData.value.saju?.mySaju?.sajugangi === sajugangi) {
      editingId.value = null;
      expandedId.value = null;
      resetSajuInputs();
      return;
    }

    const saveParams = {
      guestUserName: f.userName || '',
      gender: f.gender === '1' ? 'MEN' : 'WOMEN',
      birthday: `${f.year}-${f.month.padStart(2, '0')}-${f.day.padStart(2, '0')}`,
      birth,
      birthTime: isBirthTimeUnknown ? '25:00' : `${hour}:${min}`,
      solar: rd.solar,
      lunar: rd.lunar,
      sajugangi,
      luck,
      isLuna: isLunar,
      isLeapYear: f.isLeapYear || null,
      isMain: true,
      serviceCategory: 'DAILY_FORTUNE_CALENDAR',
      option1: f.option1,
      option2: f.option2,
      option4: f.option4,
      blNum: rd.transdata?.lucknum,
    };

    await userGroupSave2(saveParams, { isMySaju: true });

    // 내 사주 스토어 갱신 (궁합 이동 시 최신 mySajuId 사용)
    await primarySajuStore.fetchMySaju();

    // 성공: 완료 처리
    completed.value.saju = true;
    expandedId.value = null;
    editingId.value = null;
    savedData.value.saju = {
      label: sajuResultSummary.value,
      sajuganji: sajuResult.value.birthganji,
      mySaju: primarySajuStore.mySaju,
    };

    // 백엔드에서 자동 지급되므로 낙관적으로 수령 상태 반영
    if (!wasSajuClaimed) {
      pointsClaimed.value.saju = true;
    }

    // 사용자 정보 재조회하여 등록일/수정일 갱신
    await refreshUserDates();

    // eligibility 재조회로 실제 지급 여부 보정
    const eligibilityMap = await refreshCampaignEligibility();
    refreshCreditBar();

    const newlyClaimed = !wasSajuClaimed && !!pointsClaimed.value.saju;
    const item = ITEMS.find((i) => i.id === 'saju');

    // 캠페인이 비활성/없음: 사주 등록은 됐지만 이벤트 참여 실패 → 안내 팝업, 완료 모달/프로세스 진척 없음
    if (!wasSajuClaimed && eligibilityMap.saju?.reasonCode === 'CAMPAIGN_NOT_AVAILABLE') {
      notifyCampaignNotAvailable(item);
      return;
    }

    if (!newlyClaimed) {
      // 포인트가 새로 적립되지 않은 경우: 수정/재등록 → 스낵바만 노출
      triggerSnackbar(
        wasSajuCompleted ? '내 사주가 수정되었습니다.' : '내 사주가 다시 등록되었습니다.',
      );
    } else {
      confirmModal.value = { show: true, item };
    }
  } catch (e) {
    DEV_MODE && console.error('사주 저장 오류:', e);
    // 내 사주 월 1회 변경 제한 등 서버 메시지 노출 (detail이 문자열/객체 모두 대응)
    const data = e?.response?.data;
    const serverMsg =
      typeof data?.detail === 'string'
        ? data.detail
        : data?.detail?.message || data?.message;
    showAlert({ title: serverMsg || '사주 등록 중 오류가 발생했습니다.' });
  } finally {
    sajuSaving.value = false;
  }
};

// 수정 모드 상태
const editingId = ref(null);

// 포인트 안내 상태
const showPointGuide = ref(false);
const showPointGuideModal = ref(false);
const isMobile = ref(false);

const updateIsMobile = () => {
  isMobile.value = window.innerWidth <= 768;
};

const handleShowPointGuide = () => {
  if (isMobile.value) {
    showPointGuide.value = true;
  } else {
    showPointGuideModal.value = true;
  }
};

// 확인 모달 상태
const confirmModal = ref({
  show: false,
  item: null,
});

// ─── 계산된 값 ───
// 미수령 항목은 배너/프로그레스에서 미등록으로 간주
const completedCount = computed(() => ITEMS.filter((item) => pointsClaimed.value[item.id]).length);
const earnedPoints = computed(() =>
  ITEMS.filter((item) => pointsClaimed.value[item.id]).reduce((sum, item) => sum + item.points, 0),
);
const nextUndone = computed(() => ITEMS.find((item) => !pointsClaimed.value[item.id]) || null);
const allDone = computed(() => completedCount.value === ITEMS.length);
const profileMode = computed(() => allDone.value && celebrationDismissed.value);

/** 항목의 등록일/수정일 */
const getItemDates = (itemId) => {
  const d = savedData.value[itemId];
  if (!d) return { createdAt: null, updatedAt: null };
  return { createdAt: d.createdAt || null, updatedAt: d.updatedAt || null };
};

/** 저장 후 사용자 정보를 재조회하여 등록일/수정일을 갱신 */
const refreshUserDates = async () => {
  try {
    const result = await getUserInfoAPI();
    if (!result) return;

    // additionalInfos 날짜 갱신
    if (result.additionalInfos) {
      const codeToItemMap = {
        mbti: 'mbti',
        blood_type: 'blood',
        rh: 'blood',
        join_path: 'channel',
        interest: 'interest',
        understanding: 'understanding',
      };

      result.additionalInfos.forEach((info) => {
        const { code, createdAt, updatedAt } = info;
        const itemId = codeToItemMap[code];
        if (itemId && savedData.value[itemId]) {
          savedData.value[itemId].createdAt = createdAt;
          savedData.value[itemId].updatedAt = updatedAt;
        }
      });
    }

    // mySaju 날짜 갱신
    if (result.mySaju && savedData.value.saju) {
      savedData.value.saju.createdAt = result.mySaju.createdAt;
      savedData.value.saju.updatedAt = result.mySaju.updatedAt;
    }
  } catch (e) {
    DEV_MODE && console.error('refreshUserDates error:', e);
  }
};

/** value → label 변환 (옵션 목록에서 매칭) */
const getOptionLabel = (options, value) => {
  if (!value || !options.length) return value || '';
  const found = options.find((opt) => opt.value === value);
  return found ? found.label : value;
};

/** 저장된 값이 현재 선택지에 없는 레거시 값인지 확인 */
const isLegacyValue = (options, value) => {
  if (!value || !options.length) return false;
  return !options.some((opt) => opt.value === value);
};

const channelLegacy = computed(() => {
  const v = savedData.value.channel?.value;
  return isLegacyValue(channelOptions.value, v) ? v : null;
});
const interestLegacy = computed(() => {
  const v = savedData.value.interest?.value;
  return isLegacyValue(interestOptions.value, v) ? v : null;
});
const understandingLegacy = computed(() => {
  const v = savedData.value.understanding?.value;
  return isLegacyValue(understandingOptions.value, v) ? v : null;
});

/** 저장된 값의 표시 라벨 */
const getSavedLabel = (item) => {
  const d = savedData.value[item.id];
  if (!d) return '';
  switch (item.type) {
    case 'saju':
      return d.label || '등록됨';
    case 'mbti':
      if (typeof d.mbti === 'string') return d.mbti;
      if (typeof d.mbti === 'object') return Object.values(d.mbti).join('');
      return '';
    case 'blood':
      return d.blood ? `${d.blood}형${d.rh ? ' ' + d.rh : ''}` : '';
    case 'channel':
      return isLegacyValue(channelOptions.value, d.value)
        ? `${d.value} (이전에 남겨주신 답변)`
        : getOptionLabel(channelOptions.value, d.value);
    case 'interest':
      return isLegacyValue(interestOptions.value, d.value)
        ? `${d.value} (이전에 남겨주신 답변)`
        : getOptionLabel(interestOptions.value, d.value);
    case 'understanding':
      return isLegacyValue(understandingOptions.value, d.value)
        ? `${d.value} (이전에 남겨주신 답변)`
        : getOptionLabel(understandingOptions.value, d.value);
    default:
      return '';
  }
};

/** 부가정보 선택지 타입 로드 */
const loadAdditionalInfoTypes = async () => {
  try {
    const types = await getAdditionalInfoTypesAPI();
    const sortOptions = (code) => {
      const type = types.find((t) => t.code === code);
      if (!type?.options) return [];
      return [...type.options]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(({ value, label }) => ({ value, label }));
    };
    channelOptions.value = sortOptions('join_path');
    interestOptions.value = sortOptions('interest');
    understandingOptions.value = sortOptions('understanding');
  } catch (e) {
    console.error('부가정보 타입 로드 실패', e);
  }
};

// ─── WebView / 초기화 ───
onMounted(() => {
  isWebView.value = !!window.ReactNativeWebView;
  updateIsMobile();
  window.addEventListener('resize', updateIsMobile);
});

onUnmounted(() => {
  window.removeEventListener('resize', updateIsMobile);
});

const loadUserInfo = async () => {
  if (isUserInfoLoaded.value) return;
  try {
    const result = await getUserInfoAPI();
    if (result?.additionalInfos) {
      let bloodType = '';
      let rh = '';

      // blood_type과 rh의 날짜를 임시 보관
      let bloodDates = {};
      let rhDates = {};

      result.additionalInfos.forEach((info) => {
        const { code, value, createdAt, updatedAt } = info;
        if (!value) return;

        const dates = { createdAt, updatedAt };

        switch (code) {
          case 'mbti':
            completed.value.mbti = true;
            savedData.value.mbti = { mbti: value, ...dates };
            break;
          case 'blood_type':
            completed.value.blood = true;
            bloodType = value;
            bloodDates = dates;
            break;
          case 'rh':
            rh = value;
            rhDates = dates;
            break;
          case 'join_path':
            completed.value.channel = true;
            savedData.value.channel = { value, ...dates };
            break;
          case 'interest':
            completed.value.interest = true;
            savedData.value.interest = { value, ...dates };
            break;
          case 'understanding':
            completed.value.understanding = true;
            savedData.value.understanding = { value, ...dates };
            break;
        }
      });

      if (bloodType) {
        savedData.value.blood = { blood: bloodType, rh: rh || '', ...bloodDates };
      }

      // 사주 등록 여부: mySaju 데이터가 있는 경우
      const mySaju = result.mySaju;
      if (mySaju) {
        completed.value.saju = true;
        const gender = mySaju.gender === 'MEN' ? '남' : '여';
        const birth = mySaju.solar || mySaju.birth;
        const birthTime = mySaju.birthTime === '25:00' ? '시간미상' : mySaju.birthTime;
        const age = mySaju.age != null ? mySaju.age : '';
        const ageKor = mySaju.ageKor != null ? mySaju.ageKor : '';
        const name = mySaju.guestUserName || '';
        savedData.value.saju = {
          label: `${name ? name + ' · ' : ''}${gender} ${ageKor ? ageKor + '세' : ''}${age ? '(만' + age + '세)' : ''} · ${birth || ''} ${birthTime || ''}`,
          mySaju,
          createdAt: mySaju.createdAt,
          updatedAt: mySaju.updatedAt,
        };
      } else if (result.birthday && result.birthTime) {
        // fallback: mySaju가 없는 경우 기존 방식
        completed.value.saju = true;
        const gender = result.gender === 'F' ? '여' : '남';
        const age = new Date().getFullYear() - new Date(result.birthday).getFullYear();
        savedData.value.saju = {
          label: `${result.birthday} ${gender} (만${age}세)`,
        };
      }

      // 프로필 모드 진입 여부 및 자동 펼침은 eligibility 조회 후 initMoreInfo에서 판단
    }
    isUserInfoLoaded.value = true;
  } catch (error) {
    DEV_MODE && console.error('Failed to load moreinfo data:', error);
    isUserInfoLoaded.value = true;
  }
};

/** 초기 데이터 로드 + 캠페인 처리 */
const initMoreInfo = async () => {
  await Promise.all([loadUserInfo(), loadAdditionalInfoTypes(), loadCampaigns()]);
  await refreshCampaignEligibility();

  // 내 사주가 없으면 대표사주 조회 (대표사주만 있는 경우 뷰 모드 진입에 필요)
  if (!savedData.value.saju?.mySaju) {
    await primarySajuStore.fetchPrimarySaju();
  }

  // 화면 노출 전에 축하 배너 표시 여부와 재등록 대상을 먼저 결정한다.
  // (재진입 시 빨간 축하 배너가 한 프레임 노출되는 깜빡임 방지)
  const claimedCount = ITEMS.filter((i) => pointsClaimed.value[i.id]).length;
  // 포인트는 수령했지만 데이터가 비어있는 항목 (예: 사주 삭제 후 재진입)
  const reRegisterTarget = ITEMS.find(
    (i) => pointsClaimed.value[i.id] && !completed.value[i.id],
  );
  if (claimedCount === ITEMS.length) {
    celebrationDismissed.value = true;
    // 모두 수령 완료라도 데이터가 비어있으면 해당 항목을 펼쳐 재등록을 유도한다.
    if (reRegisterTarget) {
      if (reRegisterTarget.id === 'saju' && hasPrimarySajuOnly.value) {
        handleEdit('saju');
      } else {
        expandedId.value = reRegisterTarget.id;
      }
    }
    isEligibilityLoaded.value = true;
  } else {
    isEligibilityLoaded.value = true;
    // 재등록이 필요한 항목이 있으면 우선 펼침, 없으면 첫 번째 미수령 항목 펼침
    const firstUnclaimed = reRegisterTarget || ITEMS.find((i) => !pointsClaimed.value[i.id]);
    if (firstUnclaimed) {
      // 사주 항목인데 대표사주만 있는 경우: 대표사주 뷰 모드 자동 진입
      if (firstUnclaimed.id === 'saju' && hasPrimarySajuOnly.value) {
        handleEdit('saju');
      } else {
        expandedId.value = firstUnclaimed.id;
        if (savedData.value[firstUnclaimed.id]) {
          prefillInputsFromSaved(firstUnclaimed.id);
        }
      }
    }
  }
};

onMounted(async () => {
  // 부가 정보 페이지 진입 로그 (신규 type, DB 등록 후 작동)
  addContentLogAPI('MY_moreinfo_view');
  await initMoreInfo();
});

// ─── 항목 토글 ───
const toggleItem = (itemId) => {
  if (editingId.value === itemId) {
    sajuViewMode.value = false;
    editingId.value = null;
    resetInputs();
    return;
  }
  // 등록 + 포인트 수령 완료: 수정 모드로 전환
  // 등록됐지만 포인트 미수령: 최초 등록 흐름처럼 새로 입력
  if (completed.value[itemId] && pointsClaimed.value[itemId]) {
    handleEdit(itemId);
    return;
  }
  // 사주 항목: 내 사주 없고 대표사주만 있을 때 → 대표사주 뷰 모드
  if (itemId === 'saju' && hasPrimarySajuOnly.value) {
    expandedId.value = null;
    handleEdit('saju');
    return;
  }
  const willExpand = expandedId.value !== itemId;
  expandedId.value = willExpand ? itemId : null;
  resetInputs();
  if (willExpand && savedData.value[itemId]) {
    prefillInputsFromSaved(itemId);
  }
};

const prefillInputsFromSaved = (itemId) => {
  const data = savedData.value[itemId];
  if (!data) return;
  switch (itemId) {
    case 'mbti':
      if (data.mbti) {
        const keys = ['에너지방향', '인식방식', '판단', '생활양식'];
        if (typeof data.mbti === 'string' && data.mbti.length === 4) {
          keys.forEach((key, i) => {
            mbtiInput.value[key] = data.mbti[i];
          });
        } else if (typeof data.mbti === 'object') {
          keys.forEach((key) => {
            if (data.mbti[key]) mbtiInput.value[key] = data.mbti[key];
          });
        }
      }
      break;
    case 'blood':
      bloodInput.value = data.blood || '';
      rhInput.value = data.rh || '';
      break;
    case 'channel':
      channelInput.value = data.value || '';
      break;
    case 'interest':
      interestInput.value = data.value || '';
      break;
    case 'understanding':
      understandingInput.value = data.value || '';
      break;
  }
};

const resetInputs = () => {
  mbtiInput.value = {};
  bloodInput.value = '';
  rhInput.value = '';
  channelInput.value = '';
  interestInput.value = '';
  understandingInput.value = '';
  sajuViewMode.value = false;
  resetSajuInputs();
};

/** 캠페인이 비활성/존재하지 않아 이벤트 참여 실패한 경우 안내 팝업.
 *  - 정보(사주/부가정보)는 저장됐지만 이벤트 참여만 실패 → 미수령 상태 유지, 진행 프로세스 변동 없음
 */
const notifyCampaignNotAvailable = (item) => {
  const label = item?.label || '정보';
  showAlert({
    title: `${label} 등록 완료`,
    text: `${label} 등록이 정상적으로 처리되었습니다.\n포인트 지급은 이벤트가 시작된 후 정보를 수정하신 경우에 지급됩니다.`,
  });
};

// ─── 저장 처리 ───
const handleSave = async (itemId, data) => {
  try {
    if (itemId === 'saju') {
      // 사주는 handleSajuRegister()로 처리
      return;
    }

    const isEditing = editingId.value === itemId;
    const wasClaimed = !!pointsClaimed.value[itemId];

    const apiData = buildApiData(itemId, data);
    if (apiData.length > 0) {
      await updateAdditionalInfoAPI({ data: apiData });
    }

    // 완료 처리
    completed.value[itemId] = true;
    expandedId.value = null;
    editingId.value = null;

    // 저장 데이터 기록
    savedData.value[itemId] = data;

    // 백엔드에서 자동 지급되므로 낙관적으로 수령 상태 반영
    if (!wasClaimed) {
      pointsClaimed.value[itemId] = true;
    }

    // 사용자 정보 재조회하여 등록일/수정일 갱신
    await refreshUserDates();

    // eligibility 재조회로 실제 지급 여부 보정 (이미 수령한 항목은 스킵)
    let eligibilityMap = {};
    if (!wasClaimed) {
      eligibilityMap = await refreshCampaignEligibility();
      refreshCreditBar();
    }

    const newlyClaimed = !wasClaimed && !!pointsClaimed.value[itemId];
    const item = ITEMS.find((i) => i.id === itemId);

    // 캠페인이 비활성/없음: 저장은 됐지만 이벤트 참여 실패 → 별도 안내, 완료 모달/프로세스 진척 없음
    if (!wasClaimed && eligibilityMap[itemId]?.reasonCode === 'CAMPAIGN_NOT_AVAILABLE') {
      notifyCampaignNotAvailable(item);
      return;
    }

    if (!newlyClaimed) {
      // 포인트가 새로 적립되지 않은 경우: 수정 또는 재등록 → 스낵바만 노출
      triggerSnackbar(
        isEditing
          ? `${item.label} 정보가 수정되었습니다.`
          : `${item.label} 정보가 다시 등록되었습니다.`,
      );
    } else {
      // 최초 등록 또는 포인트 신규 수령: 완료 모달
      confirmModal.value = { show: true, item };
    }
  } catch (e) {
    DEV_MODE && console.error('Save error:', e);
  }
};

const buildApiData = (itemId, data) => {
  switch (itemId) {
    case 'mbti': {
      const m = data.mbti;
      const mbtiStr = `${m['에너지방향'] || ''}${m['인식방식'] || ''}${m['판단'] || ''}${m['생활양식'] || ''}`;
      return [{ code: 'mbti', value: mbtiStr }];
    }
    case 'blood': {
      const result = [{ code: 'blood_type', value: data.blood }];
      if (data.rh) result.push({ code: 'rh', value: data.rh.toLowerCase() });
      return result;
    }
    case 'channel':
      return [{ code: 'join_path', value: data.value }];
    case 'interest':
      return [{ code: 'interest', value: data.value }];
    case 'understanding':
      return [{ code: 'understanding', value: data.value }];
    default:
      return [];
  }
};

// ─── 모달 핸들러 ───
const closeConfirmModal = () => {
  const wasDone = allDone.value;
  confirmModal.value = { show: false, item: null };
  if (wasDone) {
    celebrationDismissed.value = true;
    const harmonyPath =
      window.innerWidth <= 1024 ? '/m/saju-insight/nangman-harmony' : '/saju-service?menu=/overviewNangmanHarmony';
    const harmonyRoute = primarySajuStore.mySajuId
      ? { path: harmonyPath, query: { id: primarySajuStore.mySajuId } }
      : harmonyPath;
    router.push(harmonyRoute);
    return;
  }
};

const dismissConfirmModal = () => {
  confirmModal.value = { show: false, item: null };
  celebrationDismissed.value = true;
};

const dismissConfirmModalFromBack = () => {
  confirmModal.value = { show: false, item: null };
};

const goToNextItem = () => {
  closeConfirmModal();
  if (nextUndone.value) {
    expandedId.value = nextUndone.value.id;
    resetInputs();
    if (savedData.value[nextUndone.value.id]) {
      prefillInputsFromSaved(nextUndone.value.id);
    }
  }
};

// ─── 수정 모드 ───
const enterSajuViewMode = (source) => {
  sajuViewMode.value = true;
  sajuStep.value = 'result';
  sajuResult.value = null;
  sajuLoading.value = true;
  editingId.value = 'saju';
  nextTick(() => {
    loadSavedSajuResult(source);
  });
};

const handleEdit = (itemId) => {
  if (itemId === 'saju') {
    // 등록된 사주가 있으면 뷰 모드 (API 재조회 → 결과 화면)
    if (savedData.value.saju?.mySaju) {
      enterSajuViewMode('my');
      return;
    }
    // 내 사주는 없지만 대표사주가 있으면 대표사주 뷰 모드
    if (hasPrimarySajuOnly.value) {
      enterSajuViewMode('primary');
      return;
    }
    // 저장 데이터가 없으면 기존 동작 (입력 모드)
    editingId.value = 'saju';
    resetSajuInputs();
    return;
  }
  editingId.value = itemId;
  prefillInputsFromSaved(itemId);
};

// ─── MBTI 유효성 ───
const isMbtiComplete = computed(() => Object.keys(mbtiInput.value).length === 4);

useBackLayer({
  id: 'mypage-moreinfo-point-guide',
  isOpen: showPointGuide,
  closeFromBack: () => {
    showPointGuide.value = false;
  },
});
useBackLayer({
  id: 'mypage-moreinfo-point-guide-modal',
  isOpen: showPointGuideModal,
  closeFromBack: () => {
    showPointGuideModal.value = false;
  },
});
useBackLayer({
  id: 'mypage-moreinfo-area',
  isOpen: isPopAreaBox,
  closeFromBack: () => {
    isPopAreaBox.value = false;
  },
});
useBackLayer({
  id: 'mypage-moreinfo-ganji',
  isOpen: isPopGanjiBox,
  closeFromBack: () => {
    isPopGanjiBox.value = false;
  },
});
useBackLayer({
  id: 'mypage-moreinfo-confirm',
  isOpen: computed(() => confirmModal.value.show),
  closeFromBack: dismissConfirmModalFromBack,
});

const moreInfoLocalDepth = computed(() => {
  if (editingId.value === 'saju' && sajuStep.value !== 'input') return 2;
  if (editingId.value) return 1;
  return sajuStep.value === 'input' ? 0 : 1;
});

useBackReducer({
  id: 'mypage-moreinfo-editor',
  canUnwind: () => moreInfoLocalDepth.value > 0,
  getDepth: () => moreInfoLocalDepth.value,
  unwindOne: () => {
    if (sajuStep.value !== 'input') {
      sajuStep.value = 'input';
      sajuResult.value = null;
      sajuViewMode.value = false;
      return;
    }
    editingId.value = null;
    resetInputs();
  },
});

// ─── 뒤로가기 ───
function hasBackNow() {
  const st = window.history.state || null;
  if (st && typeof st.position === 'number') return st.position > 0;
  if (st && st.back != null) return true;
  return false;
}

const handleBack = () => {
  if (hasBackNow()) {
    router.back();
    return;
  }
  router.back();
};
</script>

<template>
  <div class="card-container moreinfo-page" :style="{ paddingBottom: `${insetBottom + 20}px` }">
    <!-- 모바일: 포인트 안내 뷰 -->
    <template v-if="showPointGuide && isMobile">
      <div class="mobile-header-wrap">
        <MobileHeader
          title="포인트 안내"
          :show-back="true"
          :show-menu="false"
          @back="showPointGuide = false"
        />
      </div>
      <div class="moreinfo-content point-guide-content">
        <MoreInfoPointGuide :items="ITEMS" :total-points="TOTAL_POINTS" />
      </div>
    </template>

    <!-- 메인 뷰 -->
    <template v-else>
      <!-- 모바일 헤더 (PC에서 숨김) -->
      <div class="mobile-header-wrap">
        <MobileHeader
          title="부가 정보"
          :show-back="isWebView"
          :show-menu="!isWebView"
          @back="handleBack"
        />
      </div>

      <!-- PC 헤더 (모바일에서 숨김) -->
      <CardHeader title="부가 정보" class="pc-card-header">
        <template v-slot:path>
          <ul>
            <li>홈</li>
            <li>마이페이지</li>
            <li>MY</li>
            <li>부가 정보</li>
          </ul>
        </template>
      </CardHeader>

      <div class="moreinfo-content">
        <!-- 로딩 -->
        <div v-if="!isUserInfoLoaded || !isEligibilityLoaded" class="moreinfo-loading">
          <v-progress-circular :size="40" :width="3" color="primary" indeterminate />
        </div>

        <template v-else>
          <!-- 진행률 헤더 -->
          <MoreInfoProgress
            :completed-count="completedCount"
            :total-count="ITEMS.length"
            :earned-points="earnedPoints"
            :total-points="TOTAL_POINTS"
            :saju-done="!!pointsClaimed.saju"
            :profile-mode="profileMode"
            @show-point-guide="handleShowPointGuide"
          />

          <!-- 항목 카드 리스트 -->
          <div class="item-list">
            <MoreInfoItemCard
              v-for="item in ITEMS"
              :key="item.id"
              :item="item"
              :completed="!!completed[item.id] && !!pointsClaimed[item.id] && editingId !== item.id"
              :points-earned="!!pointsClaimed[item.id]"
              :has-saved-data="!!completed[item.id] && !pointsClaimed[item.id]"
              :expanded="expandedId === item.id || editingId === item.id"
              :profile-mode="profileMode && editingId !== item.id"
              :saved-label="getSavedLabel(item)"
              :item-dates="getItemDates(item.id)"
              @toggle="toggleItem(item.id)"
              @edit="handleEdit(item.id)"
            >
              <!-- 사주 패널 -->
              <template v-if="item.type === 'saju'">
                <div class="panel-saju">
                  <!-- 사주 입력 폼 -->
                  <template v-if="sajuStep === 'input'">
                    <div class="manse-layout">
                      <div class="search-form-box">
                        <SajuForm
                          v-model:formData="sajuFormData"
                          v-model:areaTxt="sajuAreaTxt"
                          :isCheckboxChecked="isCheckboxChecked"
                          @search="handleSajuLookup"
                          @openAreaPopup="handleOpenAreaPopup"
                          @openGanjiPopup="handleOpenGanjiPopup"
                        />
                      </div>
                    </div>

                    <!-- 옵션 설정 (SajuOption 모바일 UI 동일 구조) -->
                    <div class="manse-layout moreinfo-option-wrap">
                      <div
                        class="search-box right mobile-option"
                        :class="{ collapsed: !sajuIsOptionInput }"
                      >
                        <div
                          class="mobile-option-header"
                          @click="sajuIsOptionInput = !sajuIsOptionInput"
                        >
                          <div class="mobile-option-header__left">
                            <span class="mobile-option-header__title">옵션 설정</span>
                          </div>
                          <button type="button" class="mobile-option-header__toggle">
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                              :style="{
                                transform: sajuIsOptionInput ? 'rotate(0deg)' : 'rotate(180deg)',
                                transition: 'transform 0.3s',
                              }"
                            >
                              <path
                                d="M5 12.5L10 7.5L15 12.5"
                                stroke="#999"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                            </svg>
                          </button>
                        </div>
                        <div v-show="sajuIsOptionInput" class="mobile-option-content">
                          <div class="mobile-option-row">
                            <div class="mobile-option-item saju-option-checkbox">
                              <input
                                id="moreinfo-chk-yaja"
                                type="checkbox"
                                class="checkbox"
                                :checked="sajuFormData.option1"
                                @change="
                                  sajuFormData = { ...sajuFormData, option1: $event.target.checked }
                                "
                              />
                              <label for="moreinfo-chk-yaja" class="label txt">야자시/조자시</label>
                            </div>
                            <div class="mobile-option-item saju-option-checkbox">
                              <input
                                id="moreinfo-chk-kortime"
                                type="checkbox"
                                class="checkbox"
                                :checked="sajuFormData.option4"
                                @change="
                                  sajuFormData = { ...sajuFormData, option4: $event.target.checked }
                                "
                              />
                              <label for="moreinfo-chk-kortime" class="label txt"
                                >한국시 적용</label
                              >
                            </div>
                          </div>
                          <div class="mobile-option-row mobile-radio-row">
                            <input
                              id="moreinfo-ra-spring"
                              type="radio"
                              class="radio"
                              name="moreinfo-ra01"
                              :value="false"
                              :checked="sajuFormData.option2 === false"
                              @change="sajuFormData = { ...sajuFormData, option2: false }"
                            /><label for="moreinfo-ra-spring" class="label font">입춘</label>
                            <input
                              id="moreinfo-ra-winter"
                              type="radio"
                              class="radio"
                              name="moreinfo-ra01"
                              :value="true"
                              :checked="sajuFormData.option2 === true"
                              @change="sajuFormData = { ...sajuFormData, option2: true }"
                            /><label for="moreinfo-ra-winter" class="label font">동지</label>
                            <input
                              id="moreinfo-ra-unknown"
                              type="radio"
                              class="radio"
                              name="moreinfo-ra01"
                              :value="null"
                              :checked="sajuFormData.option2 === null"
                              @change="sajuFormData = { ...sajuFormData, option2: null }"
                            /><label for="moreinfo-ra-unknown" class="label font">모름</label>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 버튼 -->
                    <div class="saju-btn-row">
                      <button class="saju-btn-reset" @click="resetSajuInputs">초기화</button>
                      <button
                        class="btn-save saju-btn-lookup"
                        :class="{ disabled: !canSajuLookup || sajuLoading }"
                        :disabled="!canSajuLookup || sajuLoading"
                        @click="handleSajuLookup"
                      >
                        {{ sajuLoading ? '조회 중...' : '조회하기' }}
                      </button>
                    </div>
                  </template>

                  <!-- 사주 조회 로딩 -->
                  <template v-else-if="sajuStep === 'result' && !sajuResult && sajuLoading">
                    <div class="saju-result-loading">
                      <v-progress-circular :size="36" :width="3" color="primary" indeterminate />
                      <span class="saju-result-loading-text">사주 정보를 불러오고 있어요</span>
                    </div>
                  </template>

                  <!-- 사주 조회 결과 -->
                  <template v-else-if="sajuStep === 'result' && sajuResult">
                    <div class="saju-result">
                      <!-- 요약 정보 -->
                      <div class="saju-result-summary">
                        <span v-if="hasPrimarySajuOnly" class="saju-primary-badge">
                          홈 낭만브리핑 사주
                        </span>
                        <div class="saju-result-name">
                          {{ sajuFormData.userName || '회원' }}님의 사주
                        </div>
                        <div class="saju-result-info">{{ sajuResultSummary }}</div>
                        <div v-if="hasPrimarySajuOnly" class="saju-primary-guide">
                          지금 낭만브리핑에서 보고 계신 사주예요.<br />
                          이대로 내 사주로 등록하시겠어요?
                        </div>
                      </div>

                      <!-- 사주 간지 그리드 -->
                      <div class="saju-ganji-grid">
                        <!-- 왼쪽 행 라벨 (천간/지지) -->
                        <div class="saju-ganji-row-labels">
                          <span class="saju-ganji-label">&nbsp;</span>
                          <div class="saju-ganji-pair">
                            <span class="saju-ganji-row-label">천간</span>
                            <span class="saju-ganji-row-label">지지</span>
                          </div>
                        </div>
                        <div
                          v-for="(col, colIndex) in sajuGanjiColumns"
                          :key="colIndex"
                          class="saju-ganji-col"
                          :class="{ 'daeun-col': col.label.includes('대운') }"
                        >
                          <span class="saju-ganji-label">{{ col.label }}</span>
                          <div class="saju-ganji-pair">
                            <span class="saju-ganji-cell" :class="five.getColorFive(col.sky)">{{
                              col.sky
                            }}</span>
                            <span class="saju-ganji-cell" :class="five.getColorFive(col.earth)">{{
                              col.earth
                            }}</span>
                          </div>
                        </div>
                      </div>

                      <!-- 버튼 -->
                      <div
                        class="saju-btn-row"
                        :class="{ 'saju-view-btn-row': sajuViewMode && !hasPrimarySajuOnly }"
                      >
                        <template v-if="sajuViewMode">
                          <template v-if="hasPrimarySajuOnly">
                            <button class="saju-btn-reset" @click="startNewSajuInput">
                              새로 입력
                            </button>
                            <button
                              class="btn-save saju-btn-register"
                              :class="{ disabled: sajuSaving }"
                              :disabled="sajuSaving"
                              @click="handleSajuRegister"
                            >
                              {{ sajuSaving ? '등록 중...' : '내 사주등록' }}
                            </button>
                          </template>
                          <button v-else class="saju-btn-new-register" @click="startNewSajuInput">
                            새 사주등록
                          </button>
                        </template>
                        <template v-else>
                          <button class="saju-btn-reset" @click="resetSajuInputs">다시 입력</button>
                          <button
                            class="btn-save saju-btn-register"
                            :class="{ disabled: sajuSaving }"
                            :disabled="sajuSaving"
                            @click="handleSajuRegister"
                          >
                            {{ sajuSaving ? '등록 중...' : '이 사주를 내 사주로 등록하기' }}
                          </button>
                        </template>
                      </div>
                    </div>
                  </template>
                </div>
              </template>

              <!-- MBTI 패널 -->
              <template v-else-if="item.type === 'mbti'">
                <div class="panel-mbti">
                  <div class="mbti-grid">
                    <div v-for="(val, key) in MBTI_MAP" :key="key" class="mbti-category">
                      <div class="mbti-cat-label">{{ val.label }}</div>
                      <div class="mbti-opts">
                        <button
                          v-for="[code, name] in val.opts"
                          :key="code"
                          class="opt-btn"
                          :class="{ selected: mbtiInput[key] === code }"
                          @click="mbtiInput[key] = code"
                        >
                          <span class="opt-code">{{ code }}</span>
                          <span class="opt-name">{{ name }}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                  <button
                    class="btn-save"
                    :class="{ disabled: !isMbtiComplete }"
                    :disabled="!isMbtiComplete"
                    @click="handleSave('mbti', { mbti: mbtiInput })"
                  >
                    저장
                  </button>
                </div>
              </template>

              <!-- 혈액형 패널 -->
              <template v-else-if="item.type === 'blood'">
                <div class="panel-blood">
                  <div class="blood-grid">
                    <button
                      v-for="b in BLOOD_TYPES"
                      :key="b"
                      class="opt-btn"
                      :class="{ selected: bloodInput === b }"
                      @click="bloodInput = b"
                    >
                      {{ b }}형
                    </button>
                  </div>
                  <div class="rh-row">
                    <button
                      v-for="r in RH_OPTIONS"
                      :key="r"
                      class="opt-btn rh-btn"
                      :class="{ selected: rhInput === r }"
                      @click="rhInput = rhInput === r ? '' : r"
                    >
                      {{ r }}
                    </button>
                    <span class="rh-hint">모르면 넘어가도 괜찮아요</span>
                  </div>
                  <button
                    class="btn-save"
                    :class="{ disabled: !bloodInput }"
                    :disabled="!bloodInput"
                    @click="handleSave('blood', { blood: bloodInput, rh: rhInput })"
                  >
                    저장
                  </button>
                </div>
              </template>

              <!-- 가입경로 / 사주 관심도 / 사주 이해도 패널 (선택형) -->
              <template v-else-if="item.type === 'channel'">
                <div class="panel-select">
                  <div v-if="channelLegacy" class="legacy-value-notice">
                    <span class="legacy-icon">ℹ️</span>
                    <span
                      ><strong>항목이 더 세심하게 개선</strong>되었어요. 번거로우시겠지만
                      <strong>다시 한 번 선택</strong>해주세요 🙏</span
                    >
                  </div>
                  <button
                    v-for="opt in channelOptions"
                    :key="opt.value"
                    class="select-opt"
                    :class="{ selected: channelInput === opt.value }"
                    @click="channelInput = opt.value"
                  >
                    <span class="radio-dot" :class="{ active: channelInput === opt.value }" />
                    <span>{{ opt.label }}</span>
                  </button>
                  <button
                    class="btn-save"
                    :class="{ disabled: !channelInput }"
                    :disabled="!channelInput"
                    @click="handleSave('channel', { value: channelInput })"
                  >
                    저장
                  </button>
                </div>
              </template>

              <template v-else-if="item.type === 'interest'">
                <div class="panel-select">
                  <div v-if="interestLegacy" class="legacy-value-notice">
                    <span class="legacy-icon">ℹ️</span>
                    <span
                      ><strong>항목이 더 세심하게 개선</strong>되었어요. 번거로우시겠지만
                      <strong>다시 한 번 선택</strong>해주세요 🙏</span
                    >
                  </div>
                  <button
                    v-for="opt in interestOptions"
                    :key="opt.value"
                    class="select-opt"
                    :class="{ selected: interestInput === opt.value }"
                    @click="interestInput = opt.value"
                  >
                    <span class="radio-dot" :class="{ active: interestInput === opt.value }" />
                    <span>{{ opt.label }}</span>
                  </button>
                  <button
                    class="btn-save"
                    :class="{ disabled: !interestInput }"
                    :disabled="!interestInput"
                    @click="handleSave('interest', { value: interestInput })"
                  >
                    저장
                  </button>
                </div>
              </template>

              <template v-else-if="item.type === 'understanding'">
                <div class="panel-select">
                  <div v-if="understandingLegacy" class="legacy-value-notice">
                    <span class="legacy-icon">ℹ️</span>
                    <span
                      ><strong>항목이 더 세심하게 개선</strong>되었어요. 번거로우시겠지만
                      <strong>다시 한 번 선택</strong>해주세요 🙏</span
                    >
                  </div>
                  <button
                    v-for="opt in understandingOptions"
                    :key="opt.value"
                    class="select-opt"
                    :class="{ selected: understandingInput === opt.value }"
                    @click="understandingInput = opt.value"
                  >
                    <span class="radio-dot" :class="{ active: understandingInput === opt.value }" />
                    <span>{{ opt.label }}</span>
                  </button>
                  <button
                    class="btn-save"
                    :class="{ disabled: !understandingInput }"
                    :disabled="!understandingInput"
                    @click="handleSave('understanding', { value: understandingInput })"
                  >
                    저장
                  </button>
                </div>
              </template>
            </MoreInfoItemCard>
          </div>

          <!-- 안내 문구 -->
          <div class="notice-box">
            <div class="notice-title">알아두세요 😊</div>
            <ul class="notice-list">
              <li>
                등록해주신 정보는 나에게 꼭 맞는 <strong class="notice-dark">사주 해석</strong>을
                위해 활용돼요.
              </li>
              <li>
                각 항목 포인트는 <strong class="notice-accent">처음 등록할 때 1회</strong> 드려요.
              </li>
              <li>
                입력하신 정보는 언제든 <strong class="notice-dark">변경할 수 있고</strong>, 받으신
                포인트는 그대로 유지돼요!
              </li>
            </ul>
          </div>
        </template>
      </div>

      <!-- 확인 모달 -->
      <MoreInfoConfirmModal
        :show="confirmModal.show"
        :item="confirmModal.item"
        :earned-points="earnedPoints"
        :all-done="completedCount === ITEMS.length"
        :next-item="nextUndone"
        :completed-count="completedCount"
        :total-count="ITEMS.length"
        :total-points="TOTAL_POINTS"
        @close="closeConfirmModal"
        @next="goToNextItem"
        @dismiss="dismissConfirmModal"
      />

      <Snackbar ref="snackbarRef" />

      <AlertComponent ref="alertRef" :title="dialogTitle" :text="dialogText" />

      <!-- PC: 포인트 안내 모달 -->
      <Teleport to="body">
        <div
          v-if="showPointGuideModal"
          class="point-guide-overlay"
          @click="showPointGuideModal = false"
        >
          <div class="point-guide-modal" @click.stop>
            <div class="point-guide-modal__header">
              <span class="point-guide-modal__title">포인트 안내</span>
              <button class="point-guide-modal__close" @click="showPointGuideModal = false">
                ✕
              </button>
            </div>
            <div class="point-guide-modal__body">
              <MoreInfoPointGuide :items="ITEMS" :total-points="TOTAL_POINTS" />
            </div>
          </div>
        </div>
      </Teleport>

      <!-- 출생 지역 선택 팝업 -->
      <PopArea
        v-if="isPopAreaBox"
        @close="isPopAreaBox = false"
        @selectAreaFinish="selectAreaFinish"
      />

      <!-- 사주간지 입력 팝업 -->
      <PopGanji
        v-if="isPopGanjiBox"
        @close="isPopGanjiBox = false"
        @searchinput="handleGanjiSearchInput"
      />
    </template>
  </div>
</template>

<style scoped lang="scss">
.moreinfo-page {
  background: #fdfbf9;
  min-height: 100vh;
  padding-top: calc(64px + var(--safe-area-top, 0px));
  padding-bottom: calc(var(--safe-area-bottom, 0px) + 40px);
  position: relative;
  overflow-x: hidden;
  word-break: keep-all;
}

.moreinfo-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.moreinfo-content {
  max-width: 440px;
  margin: 0 auto;
  padding: 16px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
  box-sizing: border-box;
  width: 100%;
}

.item-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

// .item-list :deep(.more-info-item-card:first-child.expanded) {
//   margin-left: -16px;
//   margin-right: -16px;
// }

/* ─── 사주 패널 ─── */
.panel-saju {
  padding: 16px 0;
}

@media (min-width: 769px) {
  .panel-saju {
    max-width: 680px;
    margin: 0 auto;
    padding: 28px 40px;
    background: #fff;
    border-radius: 16px;
    border: 1px solid #f0ece8;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  }
}

.saju-bonus-box {
  background: linear-gradient(135deg, #fff8f0, #fff0e8);
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 16px;
  border: 1px dashed #f5a623;
  display: flex;
  align-items: center;
  gap: 12px;
}

.bonus-icon {
  font-size: 28px;
  flex-shrink: 0;
}

.bonus-title {
  font-size: 14px;
  font-weight: 700;
  color: #b8860b;
  margin-bottom: 2px;
}

.bonus-desc {
  font-size: 12px;
  color: #aa8a5a;
  line-height: 1.5;
}

/* SajuForm 래퍼: 부가정보 패널용 모바일 레이아웃 강제 */
.panel-saju :deep(.manse-layout) {
  background: transparent !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
  width: 100% !important;

  /* 불필요한 요소 숨김 */
  .saju-input-mobile-header,
  .mobile-picker-btn {
    display: none !important;
  }

  /* 폼 래퍼 초기화 */
  .search-form-box {
    display: block !important;
    width: 100% !important;
    height: auto !important;
    padding: 0 !important;
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
  }

  .search-box {
    width: 100% !important;
    height: auto !important;
    display: flex !important;
    flex-direction: column !important;
    padding: 0 !important;
    background: transparent !important;
    border: none !important;
  }

  .search-box .item-box {
    background: transparent !important;
    border: none !important;
  }

  /* ── 성별 + 역법 영역 ── */
  .birth-gender-wrap {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    gap: 8px !important;
    padding: 0 !important;
    margin-top: 4px !important;
    margin-bottom: 4px !important;
  }

  @media (min-width: 769px) {
    .birth-gender-wrap {
      align-items: center !important;
      gap: 16px !important;
    }
  }

  .txt-gender-box {
    display: inline-flex !important;
    flex-direction: row !important;
    gap: 0 !important;
    margin: 0 !important;
    border: 1px solid #ebebeb !important;
    border-radius: 6px !important;
    overflow: hidden !important;
    background: #fff !important;

    input[type='radio'],
    input[type='checkbox'] {
      position: absolute !important;
      opacity: 0 !important;
      width: 0 !important;
      height: 0 !important;
      pointer-events: none !important;
    }

    .rd-orange + label {
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      height: 34px !important;
      padding: 0 10px !important;
      font-size: 13px !important;
      font-weight: 500 !important;
      color: #333 !important;
      background: transparent !important;
      border: none !important;
      border-radius: 0 !important;
      cursor: pointer !important;
      position: relative !important;
      white-space: nowrap !important;
      background-image: none !important;
      margin: 0 !important;
      flex: 1 1 0 !important;
      min-width: 0 !important;
    }

    .rd-orange + label.line {
      border-left: 1px solid #ebebeb !important;
      border-radius: 0 !important;

      &::before {
        display: none !important;
      }
    }

    .rd-orange:checked + label {
      background: #eb4c10 !important;
      color: #fff !important;
      border-color: transparent !important;
    }

    .rd-gender + label {
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      height: 34px !important;
      padding: 0 6px 0 22px !important;
      font-size: 13px !important;
      font-weight: 500 !important;
      color: #333 !important;
      background-color: transparent !important;
      background-position: 4px center !important;
      background-repeat: no-repeat !important;
      background-size: 16px 16px !important;
      border: none !important;
      border-radius: 0 !important;
      cursor: pointer !important;
      position: relative !important;
      white-space: nowrap !important;
      margin: 0 !important;
      flex: 1 1 0 !important;
      min-width: 0 !important;
    }

    .rd-gender.male + label {
      background-image: url('/src/assets/images_manse/icon_male.svg') !important;
    }
    .rd-gender.female + label {
      background-image: url('/src/assets/images_manse/icon_female.svg') !important;
    }
    .rd-gender + label.line {
      border-left: 1px solid #ebebeb !important;
      border-radius: 0 !important;

      &::before {
        display: none !important;
      }
    }
    .rd-gender:checked + label {
      background-color: #eb4c10 !important;
      color: #fff !important;
      border-color: transparent !important;
    }
    .rd-gender.male:checked + label {
      background-image: url('/src/assets/images_manse/icon_male_on.svg') !important;
    }
    .rd-gender.female:checked + label {
      background-image: url('/src/assets/images_manse/icon_female_on.svg') !important;
    }
  }

  /* 성별/역법 박스 크기 (모바일: 기존 비율 유지) */
  .txt-gender-box:first-child {
    flex: 1 1 0 !important;
    min-width: 0 !important;
  }
  .txt-gender-box:nth-child(2) {
    flex: 1.5 1 0 !important;
    min-width: 0 !important;
  }

  /* 사주간지 입력 버튼 (모바일에서는 SajuForm 기본 스타일 사용) */

  @media (min-width: 769px) {
    /* PC: 성별/역법 내용 크기에 맞춤 */
    .txt-gender-box:first-child {
      flex: 0 0 auto !important;
    }
    .txt-gender-box:nth-child(2) {
      flex: 0 0 auto !important;
    }

    .btn-right {
      flex: 0 0 auto !important;
      margin-left: auto !important;

      .btn-bg-point {
        height: 34px !important;
        padding: 0 14px !important;
        font-size: 13px !important;
        font-weight: 600 !important;
        white-space: nowrap !important;
        border-radius: 6px !important;
        border: none !important;
        background: #eb4c10 !important;
        color: #fff !important;
        cursor: pointer !important;
      }
    }
  }

  /* 시간모름 체크박스 강제 숨김 */
  .txt-year-box > .saju-option-checkbox,
  .txt-year-box > span.saju-option-checkbox {
    display: none !important;
    visibility: hidden !important;
    width: 0 !important;
    height: 0 !important;
    overflow: hidden !important;
  }

  /* ── 연월일 + 시분(아래줄) ── */
  .txt-year-box {
    display: flex !important;
    flex-wrap: wrap !important;
    gap: 4px !important;
    margin-bottom: 0 !important;
    height: auto !important;
    width: 100% !important;

    > span {
      display: block !important;
      min-width: 0 !important;
      height: 38px !important;
      overflow: hidden !important;
    }

    /* 줄바꿈용 빈 요소 */
    > .flex-break {
      flex-basis: 100% !important;
      height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      display: block !important;
    }

    /* 년/월/일 입력 필드 */
    > span.input-relative:nth-child(1),
    > span.input-relative:nth-child(2),
    > span.input-relative:nth-child(3) {
      flex: 1 1 0 !important;
      min-width: 0 !important;
    }

    /* 시/분 입력 필드 — 년/월/일과 동일하게 균등 분배 */
    > span.input-relative:nth-of-type(4),
    > span.input-relative:nth-of-type(5) {
      flex: 1 1 0 !important;
      min-width: 0 !important;
    }

    > span.input-relative {
      * {
        min-width: 0 !important;
        max-width: 100% !important;
      }

      .v-input,
      .v-input__control {
        height: 100% !important;
        width: 100% !important;
      }

      .v-field {
        border: none !important;
        border-bottom: 1px solid #c2c2c2 !important;
        border-radius: 0 !important;
        background: transparent !important;
        padding: 0 !important;
        min-height: unset !important;
        height: 100% !important;
        width: 100% !important;

        .v-field__field {
          height: 100% !important;
          width: 100% !important;
        }

        input {
          text-align: center !important;
          font-size: 16px !important;
          color: #333 !important;
          padding: 0 !important;
          height: 100% !important;
          width: 100% !important;
          flex: 1 !important;
        }

        .txt-r {
          font-size: 14px !important;
          color: #333 !important;
          padding-right: 2px !important;
          flex-shrink: 0 !important;
        }

        .v-field__outline {
          display: none !important;
        }
        .v-field__append-inner {
          padding: 0 !important;
        }
      }
    }
  }

  /* ── 이름 + 출생지역: 세로 배치 ── */
  .search-box .item-box:nth-child(3) .txt-text-box {
    display: flex !important;
    flex-direction: column !important;
    gap: 4px !important;

    > .v-text-field {
      width: 100% !important;
      flex: none !important;
    }
  }
}

/* 옵션 설정 — SajuOption 모바일 스타일 강제 적용 */
.moreinfo-option-wrap {
  margin-top: 16px;
  margin-bottom: 16px;

  .search-box.right.mobile-option {
    width: 100% !important;
    padding: 0 !important;
    background: #f8f8f8 !important;
    border-radius: 8px !important;
    border: none !important;
    gap: 0 !important;
    height: auto !important;

    &.collapsed .mobile-option-content {
      display: none !important;
    }
  }

  .mobile-option-header {
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    padding: 12px 16px !important;
    cursor: pointer !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }

  .mobile-option-header__left {
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;
  }

  .mobile-option-header__title {
    font-size: 15px !important;
    font-weight: 600 !important;
    color: #333 !important;
  }

  .mobile-option-header__toggle {
    width: 24px !important;
    height: 24px !important;
    background: none !important;
    border: none !important;
    cursor: pointer !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    padding: 0 !important;
  }

  .mobile-option-content {
    width: 100% !important;
    box-sizing: border-box !important;
    padding: 0 16px 16px !important;
  }

  .mobile-option-row {
    display: flex !important;
    align-items: center !important;
    gap: 24px !important;
    padding: 12px 0 !important;
    border-top: none !important;

    &:first-child {
      border-top: 1px solid #ebebeb !important;
      padding-top: 8px !important;
    }
  }

  .mobile-option-item {
    display: flex !important;
    align-items: center !important;
    cursor: pointer !important;

    .checkbox + .label {
      margin: 0 !important;
    }
  }

  .mobile-radio-row {
    gap: 0 !important;
    flex-wrap: nowrap !important;

    .radio {
      display: none !important;
    }

    .radio + .label {
      margin-right: 16px !important;
      font-size: 15px !important;
      white-space: nowrap !important;
    }

    .radio + .label:last-of-type {
      margin-right: 0 !important;
    }
  }

  .mobile-option-row {
    flex-wrap: nowrap !important;
  }
}

/* 버튼 행 */
.saju-btn-row {
  display: flex;
  gap: 8px;

  .btn-save {
    width: auto;
    margin-top: 0;
  }
}

.saju-btn-reset {
  flex: 1;
  padding: 14px;
  border-radius: 10px;
  border: 1.5px solid #e0d6cc;
  background: #fff;
  color: #8a7a6a;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}

.saju-btn-lookup {
  flex: 2;
}

.saju-btn-register {
  flex: 2;
}

/* 사주 결과 */
.saju-result {
  padding: 0;
}

.saju-result-summary {
  text-align: center;
  margin-bottom: 10px;
}

.saju-result-name {
  font-size: 14px;
  font-weight: 700;
  color: #3a2a1a;
  margin-bottom: 4px;
}

.saju-result-info {
  font-size: 12px;
  color: #8a7a6a;
}

.saju-primary-badge {
  display: inline-block;
  padding: 3px 10px;
  margin-bottom: 8px;
  border: 1px solid var(--primary-color, #e84c4c);
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  color: var(--primary-color, #e84c4c);
  background: #fff;
}

.saju-primary-guide {
  margin-top: 10px;
  font-size: 12px;
  line-height: 1.5;
  color: #8a7a6a;
}

/* ─── 사주 로딩 ─── */
.saju-result-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px 0;
}

.saju-result-loading-text {
  font-size: 13px;
  color: #8a7a6a;
}

/* ─── 사주 뷰 모드 ─── */
.saju-view-btn-row {
  justify-content: center;
}

.saju-btn-new-register {
  width: 100%;
  border: 1.5px solid #e8553a;
  background: #fff;
  color: #e8553a;
  font-weight: 700;
  font-size: 15px;
  border-radius: 10px;
  padding: 14px 24px;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.2s;
}

/* 간지 그리드 */
.saju-ganji-grid {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 8px;
  padding: 0 0 8px;
  margin-bottom: 16px;

  /* 좌측 행 라벨 너비만큼 우측에 여백을 주어 컬럼들이 전체 중앙에 오도록 보정 */
  &::after {
    content: '';
    flex: 0 0 auto;
    width: var(--saju-ganji-row-labels-w, 28px);
  }
}

.saju-ganji-row-labels {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  padding-right: 4px;
}

.saju-ganji-row-label {
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-size: 12px;
  font-weight: 500;
  color: #8a7a6a;
}

.saju-ganji-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 44px;

  &.daeun-col {
    width: auto;
    min-width: 44px;

    .saju-ganji-label {
      font-size: 11px;
      white-space: nowrap;
    }
  }
}

.saju-ganji-label {
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  line-height: 1;
  font-weight: 400;
  color: #757575;
  text-align: center;
}

.saju-ganji-pair {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.saju-ganji-cell {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'ChosunGs';
  font-size: 22px;
  font-weight: 400;
  color: #fff;

  &.five01 {
    background: #c23535;
  }
  &.five02 {
    background: #404040;
  }
  &.five03 {
    background: #5e9f1c;
  }
  &.five04 {
    background: #949494;
  }
  &.five05 {
    background: #db9823;
  }
  &.five06 {
    background: #ddd;
  }
}

/* ─── MBTI 패널 ─── */
.panel-mbti {
  padding: 12px 0;
}

@media (min-width: 769px) {
  .panel-mbti {
    max-width: 680px;
    margin: 0 auto;
    padding: 28px 40px;
    background: #fff;
    border-radius: 16px;
    border: 1px solid #f0ece8;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  }
}

.mbti-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.mbti-cat-label {
  font-size: 11px;
  color: #8a7a6a;
  margin-bottom: 6px;
  font-weight: 500;
}

.mbti-opts {
  display: flex;
  gap: 6px;
}

.mbti-opts .opt-btn {
  flex: 1;
  padding: 10px 4px;
  font-size: 13px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.opt-code {
  font-weight: 700;
  font-size: 16px;
}

.opt-name {
  font-size: 11px;
}

/* ─── 혈액형 패널 ─── */
.panel-blood {
  padding: 12px 0;
}

@media (min-width: 769px) {
  .panel-blood {
    max-width: 680px;
    margin: 0 auto;
    padding: 28px 40px;
    background: #fff;
    border-radius: 16px;
    border: 1px solid #f0ece8;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  }
}

.blood-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 12px;
}

.rh-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.rh-btn {
  flex: 1;
  font-size: 13px;
}

.rh-hint {
  flex: 2;
  font-size: 11px;
  color: #aa9a8a;
  padding-left: 4px;
}

/* ─── 선택형 패널 공통 ─── */
.legacy-value-notice {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 10px 14px;
  background: #fff8ec;
  border: 1px solid #f5deb3;
  border-radius: 10px;
  font-size: 13px;
  color: #7a6a52;
  line-height: 1.5;
  margin-bottom: 4px;

  .legacy-icon {
    flex-shrink: 0;
  }

  strong {
    color: #c8860a;
    font-weight: 600;
  }
}

.panel-select {
  padding: 12px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

@media (min-width: 769px) {
  .panel-select {
    max-width: 680px;
    margin: 0 auto;
    padding: 28px 40px;
    background: #fff;
    border-radius: 16px;
    border: 1px solid #f0ece8;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  }
}

.select-opt {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 14px;
  cursor: pointer;
  font-family: inherit;
  border: 1.5px solid #e0d6cc;
  background: #fdfbf9;
  color: #5a4a3a;
  text-align: left;
  transition: all 0.2s ease;

  &.selected {
    border-color: #e8553a;
    background: #fff5f2;
    color: #e8553a;
    font-weight: 600;
  }
}

.radio-dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 2px solid #d0c6bc;
  background: #fff;
  position: relative;
  transition: all 0.2s ease;

  &.active {
    border-color: #e8553a;

    &::after {
      content: '';
      position: absolute;
      top: 3px;
      left: 3px;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #e8553a;
    }
  }
}

/* ─── 공통 버튼 ─── */
.opt-btn {
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 14px;
  cursor: pointer;
  font-family: inherit;
  border: 1.5px solid #e0d6cc;
  background: #fdfbf9;
  color: #5a4a3a;
  text-align: center;
  transition: all 0.2s ease;

  &.selected {
    border: 2px solid #e8553a;
    background: #fff5f2;
    color: #e8553a;
    font-weight: 600;
  }
}

.btn-save {
  width: 100%;
  margin-top: 16px;
  padding: 14px;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #e8553a, #d4442b);
  box-shadow: 0 4px 16px rgba(232, 85, 58, 0.25);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;

  &.disabled {
    background: #e0d6cc;
    color: #aa9a8a;
    box-shadow: none;
    cursor: default;
  }
}

/* ─── 안내 ─── */
.notice-box {
  background: #f8f4f0;
  border-radius: 16px;
  padding: 16px 20px;
}

.notice-title {
  font-size: 13px;
  font-weight: 600;
  color: #8a7a6a;
  margin-bottom: 10px;
}

.notice-list {
  margin: 0;
  padding-left: 20px;
  font-size: 13px;
  color: #8a7a6a;
  line-height: 1.4;
  display: flex;
  flex-direction: column;
  gap: 4px;
  list-style-type: disc;

  .notice-dark {
    color: #5a4a3a;
  }

  .notice-accent {
    color: #e8553a;
  }
}

/* ─── 포인트 안내 (모바일 뷰) ─── */
.point-guide-content {
  padding-top: 0;
}

/* ─── 포인트 안내 (PC 모달) ─── */
.point-guide-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.point-guide-modal {
  background: #f8f4f0;
  border-radius: 20px;
  width: 420px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  animation: modalSlideUp 0.3s ease;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px 0;
  }

  &__title {
    font-size: 18px;
    font-weight: 700;
    color: #3a2a1a;
  }

  &__close {
    width: 32px;
    height: 32px;
    border: none;
    background: rgba(0, 0, 0, 0.06);
    border-radius: 50%;
    font-size: 14px;
    color: #8a7a6a;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s;

    &:hover {
      background: rgba(0, 0, 0, 0.1);
    }
  }

  &__body {
    padding: 20px 24px 24px;
    overflow-y: auto;
  }
}

@keyframes modalSlideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ─── PC/모바일 헤더 전환 ─── */
.pc-card-header {
  display: none;
}

/* ─── PC 대응 ─── */
@media (min-width: 769px) {
  .moreinfo-page {
    padding-top: 0;
    min-height: auto;
    background: transparent;
  }

  .mobile-header-wrap {
    display: none;
  }

  .pc-card-header {
    display: flex;
    width: 100%;
  }

  .moreinfo-content {
    max-width: 100%;
    margin: 0;
    padding: 28px 40px;
    background: #fff;
    border-radius: 16px;
    gap: 20px;
  }

  .item-list {
    gap: 12px;
  }

  .item-list :deep(.more-info-item-card:first-child.expanded) {
    margin-left: 0;
    margin-right: 0;
  }

  .notice-box {
    padding-left: 16px;
    background: #f9f7f5;
    border: 1px solid #f0ebe6;
  }

  .notice-list {
    gap: 0px;
    line-height: 2;
  }
}
</style>
